import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Window, Dialog, EmptyState, Loading } from "../../components/VB";
import ContractDocument from "../../components/ContractDocument";
import SignaturePad from "../../components/SignaturePad";
import { toast } from "sonner";
import { Eye, Printer, PenLine, AlertCircle } from "lucide-react";

const StatusBadge = ({ status }) => {
  const map = {
    "Draft": "vb-badge-pending",
    "Menunggu Tanda Tangan": "vb-badge-pending",
    "Aktif": "vb-badge-approved",
    "Diperpanjang": "vb-badge-izin",
    "Terminated": "vb-badge-rejected",
  };
  return <span className={`vb-badge ${map[status] || "vb-badge-pending"}`}>{status}</span>;
};

export default function KontrakSaya() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  const [signTarget, setSignTarget] = useState(null);
  const [signatureData, setSignatureData] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const viewDetail = async (id) => {
    try {
      const { data } = await api.get(`/contracts/${id}`);
      setView(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitSign = async () => {
    if (!signatureData) {
      toast.error("Mohon tanda tangani terlebih dahulu");
      return;
    }
    try {
      await api.post(`/contracts/${signTarget.id}/sign`, { signature_data: signatureData });
      toast.success("Kontrak berhasil ditandatangani!");
      setSignTarget(null); setSignatureData("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (view) {
    return (
      <div>
        <div className="no-print" style={{ marginBottom: 8, display: "flex", gap: 6 }}>
          <button className="vb-btn" onClick={() => setView(null)}>← Kembali</button>
          {(view.status === "Aktif" || view.status === "Diperpanjang") && (
            <button className="vb-btn-primary" onClick={() => setTimeout(() => window.print(), 100)}>
              <Printer className="w-3 h-3 inline mr-1" />Cetak Kontrak
            </button>
          )}
        </div>
        <ContractDocument data={view} />
      </div>
    );
  }

  const pending = items.filter((c) => c.status === "Menunggu Tanda Tangan");

  return (
    <Window title="Kontrak Kerja Saya">
      {pending.length > 0 && (
        <div style={{ background: "#FEEFB3", border: "2px solid #9F6000", padding: 12, fontSize: 13, marginBottom: 12, borderRadius: 4 }}>
          <AlertCircle className="w-4 h-4 inline mr-2 text-yellow-700" />
          <strong>Perhatian:</strong> Anda memiliki <strong>{pending.length} kontrak</strong> yang menunggu tanda tangan Anda.
        </div>
      )}

      {loading ? <Loading /> : items.length === 0 ? <EmptyState message="Anda belum memiliki kontrak" /> : (
        <table className="vb-table">
          <thead>
            <tr><th>No. Kontrak</th><th>Jenis</th><th>Posisi</th><th>Periode</th><th>Status</th><th>Aksi</th></tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <td style={{ fontFamily: "Consolas, monospace", fontSize: 11 }}>{it.nomor_kontrak}</td>
                <td>{it.jenis_kontrak}</td>
                <td>{it.posisi}</td>
                <td style={{ fontSize: 11 }}>{it.tanggal_mulai} {it.tanggal_selesai ? `s/d ${it.tanggal_selesai}` : "(tidak terbatas)"}</td>
                <td><StatusBadge status={it.status} /></td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => viewDetail(it.id)} data-testid={`view-kontrak-${it.id}`}>
                    <Eye className="w-3 h-3 inline mr-1" />Lihat
                  </button>
                  {it.status === "Menunggu Tanda Tangan" && (
                    <button className="vb-btn-primary" style={{ padding: "2px 8px" }} onClick={() => setSignTarget(it)} data-testid={`sign-${it.id}`}>
                      <PenLine className="w-3 h-3 inline mr-1" />Tanda Tangani
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Sign Dialog */}
      <Dialog
        open={!!signTarget} onClose={() => { setSignTarget(null); setSignatureData(""); }}
        title="Tanda Tangan Digital Kontrak" width={580}
        footer={<>
          <button className="vb-btn" onClick={() => { setSignTarget(null); setSignatureData(""); }}>Batal</button>
          <button className="vb-btn-primary" onClick={submitSign} disabled={!signatureData} data-testid="confirm-sign">
            Submit Tanda Tangan
          </button>
        </>}
      >
        {signTarget && (
          <>
            <div style={{ background: "#FFFFE1", border: "1px solid #828790", padding: 10, fontSize: 12, marginBottom: 10 }}>
              <strong>{signTarget.nomor_kontrak}</strong><br />
              {signTarget.jenis_kontrak} - {signTarget.posisi}<br />
              Periode: {signTarget.tanggal_mulai} {signTarget.tanggal_selesai ? `s/d ${signTarget.tanggal_selesai}` : "(tidak terbatas)"}<br />
              <em style={{ fontSize: 11, color: "#666" }}>Pastikan Anda telah membaca isi kontrak sebelum menandatangani.</em>
            </div>
            <p style={{ fontSize: 12 }}>
              Tanda tangan Anda di bawah ini:
            </p>
            <SignaturePad onSave={setSignatureData} width={520} height={180} />
            {signatureData && (
              <div style={{ marginTop: 8, padding: 8, background: "#dff2bf", border: "1px solid #4f8a10", fontSize: 12 }}>
                ✓ Tanda tangan siap. Klik "Submit Tanda Tangan" untuk menyetujui kontrak.
              </div>
            )}
            <div style={{ fontSize: 11, color: "#9F6000", marginTop: 8, fontStyle: "italic" }}>
              ⚠ Dengan menandatangani secara digital, Anda menyatakan setuju dengan seluruh isi kontrak dan data profil kontrak Anda akan otomatis diperbarui.
            </div>
          </>
        )}
      </Dialog>
    </Window>
  );
}
